import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["content", "addonPrice", "percentageIncrease", "priceVariantDiv", "modificationDiv", "priceVariantInput", "modificationInput", "customSizeInput",  "seriesModification", "seriesId"]

    connect() {
        this.rowId = this.element.dataset.rowId
    }


    changeVariant(event) {
        const seriesMod = this.hasSeriesModificationTarget && this.seriesModificationTarget.value === "true"
        if (!seriesMod) return

        const el = event.currentTarget
        const raw = (el.dataset.variantName || el.value || "").toString()
        const variant = raw.split(/[–-]₱/)[0]?.trim()
        if (!variant) return
        const frame = document.getElementById("series_mods")
        if (!frame) return

        const u = new URL("products/series_modifications", window.location.origin)
        u.searchParams.set("variant", variant)

        if (this.hasSeriesIdTarget && this.seriesIdTarget.value) {
            u.searchParams.set("series_id", this.seriesIdTarget.value)
        }

        frame.src = u.toString()
    }
    applyCustomSize(e) {
        const percentage = e?.detail?.percentage ?? parseFloat(this.percentageIncreaseTarget?.value || 0)

        // If invalid, reset UI and values, then bail
        if (isNaN(percentage) || percentage < 0) {
            this.priceVariantDivTargets.forEach((el) => {
                const basePrice = parseFloat(el.dataset.basePrice)
                const radioInput = el.querySelector("input[type='radio']")
                const increasedPriceEl = el.querySelector(".modal-increased-price")
                const increaseWrap = el.querySelector(".modal-increase-wrap")
                const percentEl = el.querySelector(".modal-increase-percent")

                if (increasedPriceEl) increasedPriceEl.textContent = ""
                if (percentEl) percentEl.textContent = "0"
                if (increaseWrap) increaseWrap.classList.add("hidden")

                if (!isNaN(basePrice) && radioInput) {
                    radioInput.value = `${basePrice}`
                }
            })
            return
        }

        // Valid percentage -> compute and show increases
        this.priceVariantDivTargets.forEach((el) => {
            const basePrice = parseFloat(el.dataset.basePrice)
            const radioInput = el.querySelector("input[type='radio']")
            const increasedPriceEl = el.querySelector(".modal-increased-price")
            const increaseWrap = el.querySelector(".modal-increase-wrap")
            const percentEl = el.querySelector(".modal-increase-percent")

            if (isNaN(basePrice) || !radioInput) return

            const newPrice = basePrice + (basePrice * percentage / 100)
            const formattedNewPrice = `₱${newPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`

            // Update visuals
            if (increasedPriceEl) {
                // keep the inline arrow, price colored via CSS class
                increasedPriceEl.textContent = `→ ${formattedNewPrice}`
            }
            if (percentEl) {
                percentEl.textContent = percentage.toString()
            }
            if (increaseWrap) {
                increaseWrap.classList.remove("hidden")
            }

            // Update the radio's submitted value
            radioInput.value = `${newPrice}`
        })

        const mods = this.modificationDivTargets.filter(el => {
            const cat = (el.dataset.modCategory || "").trim();
            return cat === "Door Components" || cat === "Door Stiles";
        });

        if (isNaN(percentage) || percentage < 0) {
            // Reset UI & values for Door Components mods
            mods.forEach((el) => {
                const basePrice = parseFloat(el.dataset.basePrice)
                const modName   = el.dataset.modName
                const input     = el.querySelector("input")
                const increasedPriceEl = el.querySelector(".modal-increased-price")
                const increaseWrap     = el.querySelector(".modal-increase-wrap")

                if (increasedPriceEl) increasedPriceEl.textContent = ""
                if (increaseWrap)     increaseWrap.classList.add("hidden")

                if (!isNaN(basePrice) && input) {
                    // Keep raw numeric in the submitted value for consistency with your parser
                    input.value = `${basePrice}`
                }
            })
            return
        }

        // Valid percentage -> compute and show increases
        mods.forEach((el) => {
            const basePrice = parseFloat(el.dataset.basePrice)
            const modName   = el.dataset.modName
            const input     = el.querySelector("input")
            const increasedPriceEl = el.querySelector(".modal-increased-price")
            const increaseWrap     = el.querySelector(".modal-increase-wrap")
            const percentEl        = el.querySelector(".modal-increase-percent")

            if (isNaN(basePrice) || !input) return

            const newPrice = basePrice + (basePrice * percentage / 100)
            const formattedNewPrice = `₱${newPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`

            if (increasedPriceEl) increasedPriceEl.textContent = `→ ${formattedNewPrice}`
            if (percentEl)        percentEl.textContent = percentage.toString()
            if (increaseWrap)     increaseWrap.classList.remove("hidden")

            // Submit raw numeric (no peso symbol) to match your existing "name–price" parsing
            input.value = `${modName}–${newPrice}`
        })
    }

    toggleModQty(e) {
        // Called on checkbox change
        const label = e.currentTarget.closest("label")
        const qtyInput = label?.querySelector('[data-modal-target="modQtyInput"]')
        if (!qtyInput) return
        if (e.currentTarget.checked) {
            qtyInput.disabled = false
            if (!qtyInput.value || parseInt(qtyInput.value, 10) < 1) qtyInput.value = 1
            qtyInput.focus()
        } else {
            qtyInput.disabled = true
        }
    }

    applyModifications() {
        let addonPrice = this.addonPriceTarget.value > 0 ? this.addonPriceTarget.value : 0.0
        // 1) collect values
        const checkedVariant = this.priceVariantInputTargets.find(r => r.checked);
        const variantPrice = parseFloat(checkedVariant?.value ?? 0);
        const variantName  = checkedVariant?.dataset.variantName || ""
        const percentageIncrease = this.hasPercentageIncreaseTarget ? parseFloat(this.percentageIncreaseTarget.value || 0.0) : 0.0
        // For checkboxes, include qty; for radios, just value
        const selectedMods = this.modificationInputTargets
            .filter(inp => inp.checked)
            .map(inp => {
                if (inp.type === "checkbox") {
                    const label = inp.closest("label")
                    const qtyEl = label?.querySelector('[data-modal-target="modQtyInput"]')
                    const qty = Math.max(1, parseInt(qtyEl?.value ?? "1", 10) || 1)
                    return { value: inp.value, qty }
                } else {
                    // radio group: keep as simple string for backward-compat within that category
                    return inp.value
                }
            })

        const customSize =
            this.hasCustomSizeInputTarget ? (this.customSizeInputTarget.value || "").trim() : ""

        // 2) find the row controller
        const row = document.getElementById(this.element.dataset.rowId)
        const rowCtrl = this.application.getControllerForElementAndIdentifier(row, "quotation-lines-row")
        // 3) pass payload
        rowCtrl?.applyModifications({
            variantPrice: variantPrice,
            variantName: variantName,
            mods: selectedMods,
            addonPrice: addonPrice,
            percentageIncrease: percentageIncrease,
            customSize
        })

        // 4) close modal
        const modal = document.getElementById("modifications-modal")
        modal?.classList.add("hidden")
        modal?.classList.remove("flex")
    }
    close() {
        this.element.classList.add("hidden")
        this.element.classList.remove("flex")
    }
}